import { useEffect, useMemo, useState } from "react";

import {
  createLoomAirshowPresetApplyCommand,
  loadBundledLoomAirshowPresetCatalog,
  type LoomAirshowCatalogState,
  type LoomAirshowPresetEntry,
  type LoomAirshowSupportState,
} from "./loomAirshowPresets";
import type { LoomAuthoringCommand, LoomAuthoringDocument } from "./loomAuthoringModel";
import type { LoomCommandAcceptance } from "./loomAuthoringHistory";
import "./loom-airshow-presets.css";

type SupportFilter = "all" | "derived" | "source" | "invalid";

interface LoomAirshowPresetBrowserProps {
  catalogState?: LoomAirshowCatalogState;
  disabled?: boolean;
  document: LoomAuthoringDocument;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onSelect: (nodeId: string) => void;
}

const FILTERS: readonly { id: SupportFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "derived", label: "Derived runnable" },
  { id: "source", label: "Source only" },
  { id: "invalid", label: "Invalid" },
];

function filterMatches(state: LoomAirshowSupportState, filter: SupportFilter): boolean {
  if (filter === "all") return true;
  if (filter === "derived") return state === "derived-native-preset";
  if (filter === "source") return state === "source-only";
  return state === "invalid-x3d";
}

function supportLabel(state: LoomAirshowSupportState): string {
  if (state === "derived-native-preset") return "Derived runnable";
  if (state === "source-only") return "Source only";
  return "Invalid source";
}

function searchText(entry: LoomAirshowPresetEntry): string {
  return [
    entry.id,
    entry.label,
    entry.archiveMemberPath,
    ...entry.reasons,
    ...entry.components.flatMap(({ componentId, name, type }) => [componentId, name, type]),
    ...entry.audit.blockedComponentTypes,
    ...entry.audit.droppedComponentTypes,
    ...entry.audit.polygonSectionTypes,
  ].join(" ").toLocaleLowerCase();
}

function displayScalar(value: boolean | number | string | null): string {
  if (value === null) return "null";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toPrecision(6);
  return String(value);
}

function EntryTruth({ entry }: { entry: LoomAirshowPresetEntry }) {
  const preset = entry.nativePreset;
  return (
    <div className="loom-airshow-entry-detail" data-testid={`loom-airshow-detail-${entry.id}`}>
      <dl>
        <div><dt>Support</dt><dd>{supportLabel(entry.supportState)}</dd></div>
        <div><dt>Archive member</dt><dd><code>{entry.archiveMemberPath}</code></dd></div>
        <div><dt>Source SHA-256</dt><dd><code>{entry.sourceAsset.sha256}</code></dd></div>
        <div><dt>Audit bucket</dt><dd>{entry.audit.bucket}</dd></div>
      </dl>
      {entry.reasons.length ? <section><strong>Source status</strong><ul>{entry.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></section> : null}
      {preset ? <>
        <section className="loom-airshow-derived-truth">
          <strong>Shape-normalized derived wing/body</strong>
          <p>
            One selected source wing and fuselage only. Source units unbound; uniformly scaled to a
            normalization target of 10 m body length with factor {preset.normalization.metresPerNativeUnit.toPrecision(6)} m/native unit.
          </p>
          <code>{preset.normalization.policyId}</code>
          <p>This is not an aircraft reconstruction and is not source-equivalent.</p>
        </section>
        <section data-testid={`loom-airshow-capability-${entry.id}`}>
          <strong>Adapter capability</strong>
          {preset.contractVersion === "v3" ? <>
            <p>Selected wing/body derivative adapter with an accepted-mesh/v3 receipt.</p>
            <dl>
              <div><dt>Evaluator</dt><dd><code>{preset.evaluatorId}</code></dd></div>
              <div><dt>Topology</dt><dd><code>{preset.topology}</code></dd></div>
              <div><dt>Union algorithm</dt><dd><code>{preset.unionAlgorithmId}</code></dd></div>
            </dl>
          </> : <p>Native v2 shape-normalized wing/body adapter.</p>}
        </section>
        <section>
          <strong>Selected components</strong>
          <ul>{preset.selectedComponents.map((component) => {
            const source = entry.components.find(({ componentId }) => componentId === component.componentId);
            return <li key={component.componentId}>{component.role}: {source?.name ?? component.componentId} <code>{component.componentId}</code></li>;
          })}</ul>
        </section>
        <section>
          <strong>Omissions ({preset.omittedComponents.length})</strong>
          {preset.omittedComponents.length ? <ul>{preset.omittedComponents.map((component) => (
            <li key={component.componentId}><b>{component.name}</b> · {component.type} — {component.reason}</li>
          ))}</ul> : <p>No other source components were present.</p>}
        </section>
        <section>
          <strong>Adaptations ({preset.adaptations.length})</strong>
          <ul>{preset.adaptations.map((adaptation, index) => (
            <li key={`${adaptation.kind}:${adaptation.componentId}:${index}`}>
              <b>{adaptation.kind}</b>: {displayScalar(adaptation.sourceValue)} → {displayScalar(adaptation.derivedValue)} — {adaptation.reason}
            </li>
          ))}</ul>
        </section>
      </> : <section className="loom-airshow-blocked-truth">
        <strong>Not runnable as a native preset</strong>
        <p>The source remains browseable. Its declared source-status reasons prevent a native preset apply.</p>
      </section>}
    </div>
  );
}

function AirshowEntryRow({
  disabled,
  entry,
  onApply,
}: {
  disabled: boolean;
  entry: LoomAirshowPresetEntry;
  onApply: (entry: LoomAirshowPresetEntry) => void;
}) {
  const [open, setOpen] = useState(false);
  return <details
    className={`loom-airshow-entry is-${entry.supportState}`}
    data-archive-member-path={entry.archiveMemberPath}
    data-support-state={entry.supportState}
    data-testid={`loom-airshow-row-${entry.id}`}
    open={open}
    role="listitem"
  >
    <summary onClick={(event) => { event.preventDefault(); setOpen((current) => !current); }}>
      <span><strong>{entry.label}</strong><code>{entry.archiveMemberPath}</code></span>
      <em>{supportLabel(entry.supportState)}</em>
    </summary>
    {open ? <>
      <EntryTruth entry={entry} />
      {entry.nativePreset ? <button
        data-preset-id={entry.id}
        data-testid={`loom-airshow-apply-${entry.id}`}
        disabled={disabled}
        onClick={() => onApply(entry)}
        type="button"
      >Apply derived wing/body</button> : null}
    </> : null}
  </details>;
}

export function LoomAirshowPresetBrowser({
  catalogState,
  disabled = false,
  document,
  onCommand,
  onSelect,
}: LoomAirshowPresetBrowserProps) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<SupportFilter>("all");
  const [query, setQuery] = useState("");
  const [applyError, setApplyError] = useState<string | null>(null);
  const [loadedState, setLoadedState] = useState<LoomAirshowCatalogState | null>(catalogState ?? null);
  useEffect(() => {
    if (catalogState) setLoadedState(catalogState);
  }, [catalogState]);
  useEffect(() => {
    if (!expanded || catalogState || loadedState) return;
    let active = true;
    void loadBundledLoomAirshowPresetCatalog().then((state) => { if (active) setLoadedState(state); });
    return () => { active = false; };
  }, [catalogState, expanded, loadedState]);
  const effectiveCatalogState = catalogState ?? loadedState;
  const catalog = effectiveCatalogState?.kind === "available" ? effectiveCatalogState.catalog : null;
  const indexedEntries = useMemo(() => catalog?.entries.map((entry) => ({ entry, search: searchText(entry) })) ?? [], [catalog]);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleEntries = useMemo(() => indexedEntries.filter(({ entry, search }) => (
    filterMatches(entry.supportState, filter) && (!normalizedQuery || search.includes(normalizedQuery))
  )).map(({ entry }) => entry), [filter, indexedEntries, normalizedQuery]);
  const apply = (entry: LoomAirshowPresetEntry) => {
    if (!catalog || disabled || !entry.nativePreset) return;
    try {
      const command = createLoomAirshowPresetApplyCommand(catalog, entry, document);
      const error = onCommand(command, { origin: "gui.scene", subjectIds: [entry.id, "wing", "fuselage", "vehicle"] });
      setApplyError(error);
      if (!error) onSelect("wing");
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : "Unable to apply the Airshow preset");
    }
  };
  return (
    <details
      className="loom-airshow-browser"
      data-testid="loom-airshow-browser"
      data-total-count={catalog?.entries.length ?? 0}
      data-visible-count={visibleEntries.length}
      open={expanded}
    >
      <summary onClick={(event) => { event.preventDefault(); setExpanded((current) => !current); }}>
        <span><b>Airshow source library</b><small>Browse complete corpus · apply truthful derivatives only</small></span>
        <em>{catalog ? `${catalog.entries.length} sources` : effectiveCatalogState?.kind === "unavailable" ? "unavailable" : "353 sources"}</em>
      </summary>
      {expanded ? <div className="loom-airshow-browser-body">
        <p>All downloaded Airshow files are listed. Runnable entries are selected-component, shape-normalized derivatives—not aircraft reconstructions.</p>
        {!effectiveCatalogState ? <span className="loom-airshow-loading" role="status">Loading and validating all 353 catalogue entries…</span>
          : effectiveCatalogState.kind === "unavailable" ? (
          <div className="pg-inline-error" data-testid="loom-airshow-catalog-error" role="alert">Catalogue disabled: {effectiveCatalogState.reason}</div>
        ) : <>
          <label className="loom-airshow-search">
            <span>Search all 353 source files</span>
            <input
              data-testid="loom-airshow-search"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, archive path, component or reason"
              type="search"
              value={query}
            />
          </label>
          <div aria-label="Airshow support filter" className="loom-airshow-filters" data-testid="loom-airshow-filter" role="group">
            {FILTERS.map(({ id, label }) => <button
              aria-pressed={filter === id}
              data-testid={`loom-airshow-filter-${id}`}
              key={id}
              onClick={() => setFilter(id)}
              type="button"
            >{label}</button>)}
          </div>
          <output aria-live="polite" data-testid="loom-airshow-count">{visibleEntries.length} of {effectiveCatalogState.catalog.entries.length} sources</output>
          <div className="loom-airshow-list" role="list">
            {visibleEntries.map((entry) => <AirshowEntryRow
              disabled={disabled}
              entry={entry}
              key={entry.id}
              onApply={apply}
            />)}
            {!visibleEntries.length ? <p className="loom-airshow-empty">No Airshow sources match this search and support filter.</p> : null}
          </div>
          {applyError ? <div className="pg-inline-error" data-testid="loom-airshow-apply-error" role="alert">{applyError}</div> : null}
        </>}
      </div> : null}
    </details>
  );
}
